<script setup>import { ref as _ref, computed as _computed } from 'vue';

  import { onMounted, onUnmounted } from "vue";
  import { Highlighter } from "~/jose_modern/highlighter.js";

  const props = defineProps({
    items: { type: Array, required: true },
    requireLotCodes: { type: Boolean, default: false },
  });

  let lotsByItem = _ref(
    Object.fromEntries(
      props.items.map((item, index) => [
        index,
        item.lots.map((l) => ({ lotCode: l.lot_code, quantity: l.quantity })),
      ])
    )
  );
  let showModal = _ref(false);

  function lotTotal(index) {
    return lotsByItem.value[index].reduce((sum, lot) => sum + (parseFloat(lot.quantity) || 0), 0);
  }

  function hasQuantityWarning(item, index) {
    return lotTotal(index) !== item.ordered_quantity;
  }

  function quantityWarning(item, index) {
    const diff = item.ordered_quantity - lotTotal(index);
    const suffix = diff > 0 ? "missing" : "extra";
    return `${Math.abs(diff)} ${item.unit} ${suffix}`;
  }

  const warningItems = _computed(() =>
    props.items
      .map((item, index) => ({ item, index }))
      .filter(({ item, index }) => hasQuantityWarning(item, index))
  );

  function lotCodeError(itemIndex, lot, lotIndex) {
    if (lot.lotCode !== "") {
      const firstIndex = lotsByItem.value[itemIndex].findIndex((l) => l.lotCode === lot.lotCode);
      if (firstIndex < lotIndex) return "Duplicate lot code";
      return null;
    }
    if (props.requireLotCodes) return "Lot code is required";

    const firstEmptyIndex = lotsByItem.value[itemIndex].findIndex((l) => l.lotCode === "");
    if (firstEmptyIndex < lotIndex) return "Only one lot without a code is allowed";

    return null;
  }

  function quantityError(lot) {
    if (lot.quantity !== "" && parseFloat(lot.quantity) <= 0) return "Quantity must be positive";
    return null;
  }

  function addLot(itemIndex) {
    lotsByItem.value[itemIndex].push({ lotCode: "", quantity: "" });
  }

  function removeLot(itemIndex, lotIndex) {
    lotsByItem.value[itemIndex].splice(lotIndex, 1);
  }

  const lotCodeInputEls = {};

  function setLotCodeInputRef(el, itemIndex, lotIndex) {
    const key = `${itemIndex}-${lotIndex}`;
    if (el) lotCodeInputEls[key] = el;
    else delete lotCodeInputEls[key];
  }

  function validationErrors() {
    return props.items.flatMap((item, itemIndex) =>
      lotsByItem.value[itemIndex]
        .map((lot, lotIndex) => ({ lot, lotIndex }))
        .filter(({ lot, lotIndex }) => lotCodeError(itemIndex, lot, lotIndex) || quantityError(lot))
        .map(({ lotIndex }) => `${itemIndex}-${lotIndex}`)
    );
  }

  function openModal() {
    const errorKeys = validationErrors();

    if (errorKeys.length > 0) {
      errorKeys.forEach((key) => {
        const el = lotCodeInputEls[key];
        if (el) Highlighter.highlight(el);
      });
      return;
    }

    showModal.value = true;
  }

  onMounted(() => document.addEventListener("purchase-order:receive", openModal));
  onUnmounted(() => document.removeEventListener("purchase-order:receive", openModal));

  defineExpose({ openModal });
</script>

<template>
  <div class="receive-layout">
    <div class="receive-header">
      <div>Item</div>
      <div class="lots-cols">
        <span>Lot Code</span>
        <span>Quantity</span>
        <span></span>
      </div>
    </div>

    <div v-for="(item, itemIndex) in items" :key="itemIndex" class="receive-item">
      <div class="item-info">
        <div class="item-name">{{ item.item_custom_name || item.item_name }}</div>
        <div v-if="item.item_custom_name" class="has-text-grey is-size-7">
          {{ item.item_name }}
        </div>
        <div data-received-total>
          {{ lotTotal(itemIndex) }} / {{ item.ordered_quantity }} {{ item.unit }}
        </div>
        <div
          v-if="hasQuantityWarning(item, itemIndex)"
          class="is-flex gap-1 help has-text-warning-dark"
          data-quantity-warning
        >
          <span class="icon is-small"><i class="fas fa-warning"></i></span>
          <span>{{ quantityWarning(item, itemIndex) }}</span>
        </div>
      </div>

      <div class="item-lots">
        <div
          v-for="(lot, lotIndex) in lotsByItem[itemIndex]"
          :key="`${itemIndex}-${lotIndex}`"
          class="lot-row"
          data-lot-row
        >
          <input
            :ref="(el) => setLotCodeInputRef(el, itemIndex, lotIndex)"
            v-model="lot.lotCode"
            class="input"
            type="text"
            placeholder="Lot code"
            autocomplete="off"
            :name="`items[${itemIndex}][lots][${lotIndex}][lot_code]`"
            data-lot-code-input
          />
          <div class="field has-addons mb-0">
            <div class="control is-expanded">
              <input
                v-model="lot.quantity"
                class="input"
                type="number"
                placeholder="Qty"
                autocomplete="off"
                min="0"
                step="any"
                :name="`items[${itemIndex}][lots][${lotIndex}][quantity]`"
                data-lot-quantity-input
              />
            </div>
            <div class="control">
              <a class="button is-static">{{ item.unit }}</a>
            </div>
          </div>
          <input type="hidden" :name="`items[${itemIndex}][item_id]`" :value="item.item_id" />
          <input type="hidden" :name="`items[${itemIndex}][item_type]`" :value="item.item_type" />
          <input type="hidden" :name="`items[${itemIndex}][unit]`" :value="item.unit" />
          <button
            v-if="lotsByItem[itemIndex].length > 1"
            class="button is-ghost is-danger"
            data-remove-lot
            @click="removeLot(itemIndex, lotIndex)"
          >
            <span class="icon is-small"><i class="fas fa-times"></i></span>
          </button>
          <p
            v-if="lotCodeError(itemIndex, lot, lotIndex)"
            class="help is-danger"
            data-lot-code-error
          >
            {{ lotCodeError(itemIndex, lot, lotIndex) }}
          </p>
          <p v-if="quantityError(lot)" class="help is-danger" data-lot-quantity-error>
            {{ quantityError(lot) }}
          </p>
        </div>

        <div class="add-lot-row">
          <a class="has-text-link" data-add-lot @click.prevent="addLot(itemIndex)">+ Add lot</a>
        </div>
      </div>
    </div>
  </div>

  <div v-if="showModal" class="modal is-active" data-confirm-modal>
    <div class="modal-background" @click="showModal = false"></div>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title">Confirm Receiving</p>
      </header>
      <section class="modal-card-body">
        <div v-if="warningItems.length" class="content mb-4">
          <p>These items have quantities that differ from what was ordered:</p>
          <ul>
            <li v-for="{ item, index } in warningItems" :key="index" data-modal-warning-item>
              {{ item.item_name }}: {{ lotTotal(index) }} / {{ item.ordered_quantity }}
              {{ item.unit }}
            </li>
          </ul>
        </div>
        <p>
          Are you sure you want to mark this order as received?<br />
          This will update the inventory for all <b>{{ items.length }}</b>
          items in this order.
        </p>
      </section>
      <footer class="modal-card-foot">
        <button type="submit" class="button is-primary">Confirm</button>
        <button type="button" class="button" data-cancel-button @click="showModal = false">
          Cancel
        </button>
      </footer>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import "~/jose_modern/css";

  .receive-header,
  .receive-item {
    display: grid;
    grid-template-columns: minmax(220px, 350px) 1fr;
    gap: 1rem;
    padding: 0.6rem 0;

    @include mobile {
      grid-template-columns: 1fr;
    }
  }

  .receive-header {
    font-weight: bold;
    border-bottom: 2px solid $border;
  }

  .receive-item {
    border-bottom: 1px solid $border-light;
  }

  .item-name {
    font-weight: 500;
  }

  .lots-cols,
  .lot-row {
    display: grid;
    grid-template-columns: minmax(180px, 1fr) 140px 40px;
    column-gap: 0.5rem;
    align-items: center;
  }

  .lot-row {
    row-gap: 0;

    [data-lot-code-error] {
      grid-column: 1;
      margin-top: 0;
    }

    [data-lot-quantity-error] {
      grid-column: 2;
      margin-top: 0;
    }
  }

  .lot-row + .lot-row {
    margin-top: 0.25rem;
  }

  .add-lot-row {
    margin-top: 0.5rem;
  }

  @include mobile {
    .receive-header .lots-cols {
      display: none;
    }
  }
</style>
